import React, { useEffect, useState } from 'react';
import { Form, Input, Button, message, Select, Modal, Space, Popconfirm, Tag } from 'antd';
import ResponsiveTable from '../components/ResponsiveTable';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { usersApi } from '../services/api-services';
import ImagePicker from '../components/ImagePicker';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userForm] = Form.useForm();

  // User edit state
  const [userEditModalVisible, setUserEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [userEditForm] = Form.useForm();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const loadUsers = async (page = currentPage, size = pageSize) => {
    try {
      const res: any = await usersApi.list({ page, page_size: size });
      setUsers(res.items);
      setTotal(res.total);
    } catch {}
  };

  useEffect(() => { loadUsers(); }, []);

  const handleSaveUser = async (values: any) => {
    try {
      await usersApi.create(values);
      message.success('创建成功');
      setUserModalVisible(false); userForm.resetFields(); loadUsers();
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const handleDeleteUser = async (id: number) => {
    try { await usersApi.delete(id); message.success('删除成功'); loadUsers(); } catch { message.error('删除失败'); }
  };

  const openEditUser = (record: any) => {
    setEditingUser(record);
    userEditForm.setFieldsValue({
      name: record.name,
      role: record.role,
      avatar: record.avatar || '',
    });
    setUserEditModalVisible(true);
  };

  const handleUpdateUser = async (values: any) => {
    if (!editingUser) return;
    try {
      await usersApi.update(editingUser.id, values);
      message.success('更新成功');
      setUserEditModalVisible(false); userEditForm.resetFields(); setEditingUser(null); loadUsers();
    } catch (e: any) { message.error(e.response?.data?.detail || '更新失败'); }
  };

  const roleMap: Record<string, string> = {
    super_admin: '超级管理员', admin: '管理员', app_admin: '应用管理员', user: '普通用户',
  };

  const columns = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '姓名', dataIndex: 'name', key: 'name' },
    { title: '角色', dataIndex: 'role', key: 'role', render: (r: string) => <span className="apple-tag">{roleMap[r] || r}</span> },
    { title: '专家', dataIndex: 'is_expert', key: 'is_expert', render: (v: boolean) => v ? <Tag color="green">是</Tag> : '-' },
    { title: '操作', key: 'action', render: (_: any, r: any) => (
      <Space>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditUser(r)}>编辑</Button>
        <Popconfirm title="确定删除该用户？" onConfirm={() => handleDeleteUser(r.id)} okText="删除" okButtonProps={{ danger: true }} cancelText="取消">
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )},
  ];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">用户管理</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={() => { userForm.resetFields(); setUserModalVisible(true); }}>新建用户</Button>
      </div>
      <p className="apple-page-subtitle">管理平台用户账号与角色权限</p>

      <ResponsiveTable columns={columns} dataSource={users} rowKey="id" pagination={{ current: currentPage, pageSize: pageSize, total: total, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t: number) => `共 ${t} 条`, onChange: (page: number, size: number) => { setCurrentPage(page); setPageSize(size); loadUsers(page, size); } }} />

      {/* 新建用户 Modal */}
      <Modal className="apple-modal" title="新建用户" open={userModalVisible}
        onCancel={() => { setUserModalVisible(false); userForm.resetFields(); }}
        onOk={() => userForm.submit()} width={520}>
        <Form className="apple-form" form={userForm} layout="vertical" onFinish={handleSaveUser}>
          <Form.Item name="avatar" label="头像">
            <ImagePicker shape="circle" />
          </Form.Item>
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true }]}><Input.Password /></Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]} initialValue="user">
            <Select options={[
              { label: '超级管理员', value: 'super_admin' },
              { label: '管理员', value: 'admin' },
              { label: '应用管理员', value: 'app_admin' },
              { label: '普通用户', value: 'user' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户 Modal */}
      <Modal className="apple-modal" title="编辑用户" open={userEditModalVisible}
        onCancel={() => { setUserEditModalVisible(false); userEditForm.resetFields(); setEditingUser(null); }}
        onOk={() => userEditForm.submit()} width={520} destroyOnHidden>
        <Form className="apple-form" form={userEditForm} layout="vertical" onFinish={handleUpdateUser}>
          <Form.Item name="avatar" label="头像">
            <ImagePicker shape="circle" />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={[
              { label: '超级管理员', value: 'super_admin' },
              { label: '管理员', value: 'admin' },
              { label: '应用管理员', value: 'app_admin' },
              { label: '普通用户', value: 'user' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
